/**
 * Badge Component - Consistent badge styling across the system
 * 
 * Features:
 * - Status badges (todo, in_progress, completed, on_hold, pending, etc.)
 * - Priority badges (urgent, high, medium, low)
 * - Custom variant badges (info, success, warning, danger)
 * - Consistent sizing and styling with Tailwind utilities
 * - Full dark mode support
 */

import React from 'react';

// Badge variants
export type BadgeVariant = 
  | 'status'
  | 'priority'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'default';

// Status types
export type StatusType = 
  | 'todo'
  | 'in_progress'
  | 'completed'
  | 'on_hold'
  | 'pending'
  | 'active'
  | 'planning'
  | 'closed'
  | 'upcoming'
  | 'overdue';

// Priority types
export type PriorityType = 
  | 'urgent'
  | 'high'
  | 'medium'
  | 'low';

// Badge sizes
export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  /** Variant determines the color scheme */
  variant?: BadgeVariant;
  /** Status type for status badges */
  status?: StatusType;
  /** Priority type for priority badges */
  priority?: PriorityType;
  /** Badge size */
  size?: BadgeSize;
  /** Badge content */
  children: React.ReactNode;
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Custom background color (overrides variant) */
  customColor?: string;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  status,
  priority,
  size = 'md',
  children,
  icon,
  className = '',
  customColor,
}) => {
  // Get status-specific styling
  const getStatusClasses = (status: StatusType): string => {
    const statusMap: Record<StatusType, string> = {
      todo: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
      in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-600',
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-600',
      on_hold: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-600',
      pending: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-600',
      planning: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-600',
      closed: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
      upcoming: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-600',
      overdue: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-600',
    };
    return statusMap[status] || statusMap.todo;
  };

  // Get priority-specific styling
  const getPriorityClasses = (priority: PriorityType): string => {
    const priorityMap: Record<PriorityType, string> = {
      urgent: 'bg-red-700 dark:bg-red-700 text-white border-red-800 dark:border-red-900',
      high: 'bg-red-500 dark:bg-red-600 text-white border-red-600 dark:border-red-700',
      medium: 'bg-amber-500 dark:bg-amber-600 text-white border-amber-600 dark:border-amber-700',
      low: 'bg-green-500 dark:bg-green-600 text-white border-green-600 dark:border-green-700',
    };
    return priorityMap[priority] || priorityMap.medium;
  };

  // Get variant-specific styling
  const getVariantClasses = (variant: BadgeVariant): string => {
    const variantMap: Record<BadgeVariant, string> = {
      status: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
      priority: 'bg-amber-500 dark:bg-amber-600 text-white border-amber-600 dark:border-amber-700',
      info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-600',
      success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-600',
      warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-600',
      danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-600',
      default: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
    };
    return variantMap[variant];
  };

  // Get size-specific styling
  const getSizeClasses = (size: BadgeSize): string => {
    const sizeMap: Record<BadgeSize, string> = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-xs',
      lg: 'px-3 py-1.5 text-sm',
    };
    return sizeMap[size];
  };

  // Determine which styling to use
  let colorClasses = '';
  if (status) {
    colorClasses = getStatusClasses(status);
  } else if (priority) {
    colorClasses = getPriorityClasses(priority);
  } else {
    colorClasses = getVariantClasses(variant);
  }

  const sizeClasses = getSizeClasses(size);

  // Base classes for all badges
  const baseClasses = 'inline-flex items-center gap-1.5 font-medium rounded-md border transition-all duration-200';

  return (
    <span
      className={`${baseClasses} ${sizeClasses} ${colorClasses} ${className}`}
      style={customColor ? { backgroundColor: customColor, color: 'white', borderColor: customColor } : undefined}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="capitalize leading-none">{children}</span>
    </span>
  );
};

export default Badge;
